/**
 * Which basemap the map wears, and everything that decides it.
 *
 * The stored preference (`settings.mapTheme`) is one of four; only two BASEMAPS exist, so
 * this module is the resolution from `device` / `schedule` / `light` / `dark` down to a
 * concrete `MapScheme`, plus the handful of app-drawn colors that have to flip with it.
 *
 * It is a plain module (not a rune one) because TransitMap consumes it imperatively —
 * the map instance is built once in an effect and restyled in place, so there is no
 * render pass to thread a reactive value through. `watchMapScheme` is the whole
 * subscription surface: it folds the three things that can change the answer (the
 * preference, the OS setting, the clock) into one callback.
 */
import { useSettingsStore, type MapThemePref } from '../../store/settings'
import { Colors } from '../../ui/colors'

export type MapScheme = 'light' | 'dark'

/**
 * The two OpenFreeMap styles, deliberately a pair rather than two unrelated maps: fiord
 * and positron are both low-chroma and label-light, so the MTA route colors and the 3D
 * vehicles stay the loudest thing on screen in either. (`bright` / `liberty` are the
 * fuller-color light styles and fight the route lines for attention.)
 */
export const MAP_STYLES: Record<MapScheme, string> = {
  dark: 'https://tiles.openfreemap.org/styles/fiord',
  light: 'https://tiles.openfreemap.org/styles/positron',
}

/**
 * The app's own paint that cannot be read off a single basemap.
 *
 * Everything else TransitMap draws is either an MTA color (route lines, badges) or an
 * accent that reads on both grounds (the teal selection, the magenta location dot); these
 * are the ones that were picked against a dark ground specifically — light text, black
 * halos, white casings — and would dissolve on a near-white one.
 */
export interface MapSchemePaint {
  /**
   * Station name (`stop-badges` text) and its halo — also the boarded bus route's own stop
   * names (`br-stop-labels`). Those are deliberately NOT `busLabel`: the pale blue is what
   * keeps ~15k ambient bus stops from shouting, and the stops of the route you are riding
   * are the one set that should read at a glance.
   */
  stopLabel: string
  stopLabelHalo: string
  /** Bus-stop name (`bus-stop-labels` text) and its halo. */
  busLabel: string
  busLabelHalo: string
  /** Ring around the gray station dot (`stop-circles`), which lifts it off the ground. */
  stopDotStroke: string
  /** The boarded-ride route line (`br-fill`), which rides over a white casing. */
  boardedLine: string
  /** Extruded building mass (`buildings-3d`). */
  building: string
  /**
   * How solid that mass gets once it has faded in. Below 1 on both schemes so a bus is
   * still findable behind a block it has driven past, rather than disappearing into it.
   */
  buildingOpacity: number
}

export const MAP_SCHEME_PAINT: Record<MapScheme, MapSchemePaint> = {
  dark: {
    stopLabel: '#dddddd',
    stopLabelHalo: '#000000',
    busLabel: '#9ec5e8',
    busLabelHalo: '#000000',
    stopDotStroke: '#bbbbbb',
    boardedLine: '#ffffff',
    // A step up from fiord's near-black ground, so the mass reads as shape rather than as
    // a hole; the lit faces MapLibre shades off this are what give the block its form.
    building: '#38415c',
    buildingOpacity: 0.75,
  },
  light: {
    stopLabel: '#1c1c22',
    stopLabelHalo: '#ffffff',
    // The bus blue itself, not the pale tint the dark map needed to keep it legible.
    busLabel: Colors.bus,
    busLabelHalo: '#ffffff',
    // positron's ground is a light gray and its roads are white, so a white ring is what
    // separates the dot from both.
    stopDotStroke: '#ffffff',
    boardedLine: '#1c1c22',
    // positron's own flat building fill, darkened just enough to separate one block from
    // the next now that they have sides.
    building: '#dedede',
    buildingOpacity: 0.85,
  },
}

/**
 * Where extruded buildings start and where they reach full strength.
 *
 * The tiles carry footprints from z13 and stop at z14 (everything above is overzoom), and
 * a whole borough of extrusions at z14 is both illegible and the expensive case on a
 * phone. So the layer starts at the zoom where a rider is looking at streets rather than
 * at the system, and fades in over the zoom and a half after it instead of popping.
 */
export const BUILDING_MIN_ZOOM = 14
const BUILDING_FULL_ZOOM = 15.5

/** Zoom fade for `fill-extrusion-opacity`, topping out at the scheme's own opacity. */
export function buildingOpacityExpression(scheme: MapScheme): unknown {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    BUILDING_MIN_ZOOM,
    0,
    BUILDING_FULL_ZOOM,
    MAP_SCHEME_PAINT[scheme].buildingOpacity,
  ]
}

/** Apply the scheme-dependent paint to a live map. Safe before a layer exists. */
export function applyMapSchemePaint(map: any, scheme: MapScheme): void {
  const p = MAP_SCHEME_PAINT[scheme]
  const set = (layer: string, prop: string, value: unknown) => {
    if (map?.getLayer?.(layer)) map.setPaintProperty(layer, prop, value)
  }
  set('stop-badges', 'text-color', p.stopLabel)
  set('stop-badges', 'text-halo-color', p.stopLabelHalo)
  set('bus-stop-labels', 'text-color', p.busLabel)
  set('bus-stop-labels', 'text-halo-color', p.busLabelHalo)
  set('stop-circles', 'circle-stroke-color', p.stopDotStroke)
  set('br-fill', 'line-color', p.boardedLine)
  set('br-stop-labels', 'text-color', p.stopLabel)
  set('br-stop-labels', 'text-halo-color', p.stopLabelHalo)
  // Ferry landings are named like stations (there are 25, not 15k bus stops to keep quiet).
  set('ferry-landing-badges', 'text-color', p.stopLabel)
  set('ferry-landing-badges', 'text-halo-color', p.stopLabelHalo)
  set('ferry-landings', 'circle-stroke-color', p.stopDotStroke)
  // LIRR / Metro-North stations are named like subway stations, for the ferry's reason.
  set('rail-station-labels', 'text-color', p.stopLabel)
  set('rail-station-labels', 'text-halo-color', p.stopLabelHalo)
  // NJ Transit stops, likewise: a stop name is a stop name whichever agency runs it.
  set('njbus-stop-labels', 'text-color', p.stopLabel)
  set('njbus-stop-labels', 'text-halo-color', p.stopLabelHalo)
  set('buildings-3d', 'fill-extrusion-color', p.building)
  set('buildings-3d', 'fill-extrusion-opacity', buildingOpacityExpression(scheme))
}

// ---------------------------------------------------------------------------
// Late-night service
// ---------------------------------------------------------------------------
// The MTA's own late-night band on the subway map: midnight to 6 AM, when several lines
// run local, stop short, or don't run at all. Half-open [0, 6) so 6:00 is already day.
export const LATE_NIGHT_FROM_HOUR = 0
export const LATE_NIGHT_TO_HOUR = 6

// Service hours are New York's, not the device's: a rider checking NYC trains from another
// time zone still wants the map that matches the service running. Built once (the
// constructor is the expensive part) and null on a runtime without the tz database, where
// every reader falls back to local time.
const NYC_CLOCK: Intl.DateTimeFormat | null = (() => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hourCycle: 'h23', // else midnight formats as "24" on some engines
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return null
  }
})()

export interface Clock {
  hour: number
  minute: number
  second: number
}

/** Wall clock in New York at `at`. */
export function nycClock(at: Date): Clock {
  if (!NYC_CLOCK) return { hour: at.getHours(), minute: at.getMinutes(), second: at.getSeconds() }
  const parts = NYC_CLOCK.formatToParts(at)
  const num = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0')
  return { hour: num('hour') % 24, minute: num('minute'), second: num('second') }
}

/** Whether `at` falls inside the subway's late-night hours. */
export function isLateNightService(at: Date): boolean {
  const { hour } = nycClock(at)
  return hour >= LATE_NIGHT_FROM_HOUR && hour < LATE_NIGHT_TO_HOUR
}

/**
 * Milliseconds until the schedule mode would next change its answer.
 *
 * Capped at an hour so a re-check is never more than that away: the two DST switches
 * happen at 2 AM — inside the late-night window — and shift the true distance to the 6 AM
 * boundary by a whole hour in one direction or the other. Re-arming hourly costs a timer
 * that does nothing and removes the case where the map is stuck on the wrong style for an
 * hour twice a year.
 */
export function msUntilLateNightFlip(at: Date): number {
  const { hour, minute, second } = nycClock(at)
  const nextBoundaryHour = hour < LATE_NIGHT_TO_HOUR ? LATE_NIGHT_TO_HOUR : 24 + LATE_NIGHT_FROM_HOUR
  const seconds = (nextBoundaryHour - hour) * 3600 - minute * 60 - second
  // +1 s so the timer lands past the boundary rather than exactly on it.
  return Math.min(Math.max(1, seconds + 1) * 1000, 60 * 60 * 1000)
}

/**
 * Whether the OS is asking for dark.
 *
 * ⚠️ In the Android WebView `prefers-color-scheme` tracks the *app's* night mode, not the
 * system's, unless the host activity uses a DayNight theme (or allows algorithmic
 * darkening). If the device setting ever appears to be ignored in a native build, that is
 * the Android theme, not this. Missing `matchMedia` (never, in a Capacitor WebView) reads
 * as light, matching a plain browser's default.
 */
export function devicePrefersDark(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}

/** Resolve a stored preference to the scheme the map should actually be in. */
export function resolveMapScheme(
  pref: MapThemePref,
  ctx: { at: Date; deviceDark: boolean },
): MapScheme {
  switch (pref) {
    case 'light':
      return 'light'
    case 'dark':
      return 'dark'
    case 'schedule':
      return isLateNightService(ctx.at) ? 'dark' : 'light'
    case 'device':
    default:
      return ctx.deviceDark ? 'dark' : 'light'
  }
}

/** The scheme right now, from the live preference / OS setting / clock. */
export function currentMapScheme(): MapScheme {
  return resolveMapScheme(useSettingsStore.getState().mapTheme, {
    at: new Date(),
    deviceDark: devicePrefersDark(),
  })
}

/**
 * Call `onChange` whenever the resolved scheme changes, from whichever of the three
 * inputs moved. Returns the unsubscribe.
 *
 * The timer is armed only in `schedule` mode and re-armed after every evaluation, so
 * switching away from it stops the clock and switching to it starts one.
 */
export function watchMapScheme(onChange: (scheme: MapScheme) => void): () => void {
  let current = currentMapScheme()
  let timer: ReturnType<typeof setTimeout> | undefined
  const media = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null

  const arm = () => {
    clearTimeout(timer)
    timer = undefined
    if (useSettingsStore.getState().mapTheme !== 'schedule') return
    timer = setTimeout(settle, msUntilLateNightFlip(new Date()))
  }

  function settle() {
    arm()
    const next = currentMapScheme()
    if (next === current) return
    current = next
    onChange(next)
  }

  const unsubSettings = useSettingsStore.subscribe((s, prev) => {
    if (s.mapTheme !== prev.mapTheme) settle()
  })
  media?.addEventListener('change', settle)
  arm()

  return () => {
    clearTimeout(timer)
    unsubSettings()
    media?.removeEventListener('change', settle)
  }
}
